import React from 'react';
import PropTypes from 'prop-types';
import uuid from 'uuid';
import cx from 'classnames';
import { FeatureFlag } from '@twigeducation/unleash-client-react';
import { ThemeProvider } from 'styled-components';
import { theme, middleSchoolTheme } from '@twigeducation/ts-fe-components';
import DrawingToolOptions from './DrawingToolOptions';
import DrawingToolDragDrop from './DrawingToolOptions/DrawingToolDragDrop';
import DrawingToolModal from './DrawingToolModal';
import Camera from './tools/Camera';
import ImageUpload from './tools/ImageUpload';
import { fillOptions, stampOptions, strokeOptions, strokeWidthOptions, textOptions } from './options';
import formatColor from './formatColor';
import Fill from './icons/Fill';
import Stroke from './icons/Stroke';
import stampIcon from './icons/stamp.svg';
import closeIcon from './icons/close-icon.svg';
import { Translation } from '../Translation';
import './scss/DrawingTool.scss';
import DrawingToolButton from './DrawingToolButton/DrawingToolButton';
import SvgSelect from './icons/components/SvgSelect';
import SvgDuplicate from './icons/components/SvgDuplicate';
import SvgPencil from './icons/components/SvgPencil';
import SvgLine from './icons/components/SvgLine';
import SvgArrow from './icons/components/SvgArrow';
import SvgBringFront from './icons/components/SvgBringFront';
import SvgCamera from './icons/components/SvgCamera';
import SvgCircle from './icons/components/SvgCircle';
import SvgCurved from './icons/components/SvgCurved';
import SvgDelete from './icons/components/SvgDelete';
import SvgImage from './icons/components/SvgImage';
import SvgRectangle from './icons/components/SvgRectangle';
import SvgSendBack from './icons/components/SvgSendBack';
import SvgUndo from './icons/components/SvgUndo';
import SvgRedo from './icons/components/SvgRedo';
import SvgFullScreen from './icons/components/SvgFullScreen';
import SvgExitFullScreen from './icons/components/SvgExitFullScreen';
import SvgLineThickness from './icons/components/SvgLineThickness';
import textIcon from './icons/text.svg';

// size which is used as a basline to scale from
const baseCanvasSize = 800;

const getPixelPropertyValue = (element, property) => {
    return parseInt(window.getComputedStyle(element).getPropertyValue(property).replace('px', ''), 10);
};

class DrawingTool extends React.Component {
    constructor(props) {
        super(props);
        const { saveToStore, predeterminedImageUrls } = props;
        this.id = uuid();
        this.element = null;
        this.state = {
            activeTool: 'select',
            loading: true,
            error: false,
            fabric: null,
            fauxFullscreenActive: false,
            fillColor: fillOptions.find(option => option.value === 'rgba(0, 0, 0, 0)'),
            isFullscreen: false,
            strokeColor: strokeOptions.find(option => option.value === 'rgb(0, 0, 0)'),
            strokeWidth: strokeWidthOptions[0],
            textSize: textOptions[2],
            selectedStamp: predeterminedImageUrls.length ? stampOptions(predeterminedImageUrls)[0] : null,
            elementInitialized: false,
        };

        this.saveToStore = saveToStore.bind(this);
        this.undoStack = [];
        this.redoStack = [];

        this.bringToFront = this.bringToFront.bind(this);
        this.changeStrokeWidth = this.changeStrokeWidth.bind(this);
        this.changeFillColor = this.changeFillColor.bind(this);
        this.changeStamp = this.changeStamp.bind(this);
        this.changeStrokeColor = this.changeStrokeColor.bind(this);
        this.changeTextSize = this.changeTextSize.bind(this);
        this.clear = this.clear.bind(this);
        this.deactivateCameraModal = this.deactivateCameraModal.bind(this);
        this.deactivateImageUploadModal = this.deactivateImageUploadModal.bind(this);
        this.delete = this.delete.bind(this);
        this.exitFullscreen = this.exitFullscreen.bind(this);
        this.goFullscreen = this.goFullscreen.bind(this);
        this.redo = this.redo.bind(this);
        this.saveCanvasState = this.saveCanvasState.bind(this);
        this.sendToBack = this.sendToBack.bind(this);
        this.setCanvasSize = this.setCanvasSize.bind(this);
        this.undo = this.undo.bind(this);
        this.getAvailableWidth = this.getAvailableWidth.bind(this);
        this.getAvailableHeight = this.getAvailableHeight.bind(this);
        this.onRefChange = this.onRefChange.bind(this);
        this.handleFullscreenChange = this.handleFullscreenChange.bind(this);
    }

    async componentDidMount() {
        await this.initialiseCanvas();
        this.initialiseCanvasEvents();
        this.setCanvasSize();
        await this.setInitialState(this.props);

        this.canvas.on('object:modified', () => {
            this.saveCanvasState();
        });

        window.addEventListener('resize', this.setCanvasSize);

        const { onReady } = this.props;
        if (onReady) onReady();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        const { initialState } = this.props;
        if (nextProps.initialState !== initialState) {
            this.setInitialState(nextProps);
        }
    }

    async componentWillUnmount() {
        document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    }

    handleFullscreenChange() {
        this.setState({ isFullscreen: !document.fullscreenElement });
    }

    onRefChange(node) {
        this.element = node;
        const { elementInitialized } = this.state;
        if (this.element && !elementInitialized) {
            this.element.tabIndex = 1000;
            this.element.addEventListener('keydown', e => {
                if ((e.keyCode || e.which) === 46 || (e.keyCode || e.which) === 8) {
                    this.delete();
                }
            });
            document.addEventListener('fullscreenchange', this.handleFullscreenChange);
            this.setState({ elementInitialized: true });
        }
    }

    setActiveTool(activeTool) {
        const { activeTool: stateActiveTool, tools } = this.state;

        if (this.activeToolInst) {
            this.activeToolInst.deactivate();
            this.activeToolInst = undefined;
        }
        // Reset colours when changing from text to a shape
        if (stateActiveTool === 'text' && activeTool !== 'text') {
            const black = fillOptions.find(color => color.value === 'rgb(0, 0, 0)');
            const transparent = strokeOptions.find(color => color.value === 'rgba(0, 0, 0, 0)');
            this.setState({
                fillColor: transparent,
                strokeColor: black,
            });
        }

        this.setState({ activeTool }, () => {
            const Tool = tools[activeTool];
            if (Tool) {
                this.activeToolInst = new Tool(this.canvas, this.state, this.saveCanvasState);
            }
        });
    }

    getAvailableWidth() {
        return (
            this.drawingToolContainer &&
            this.drawingToolContainer.clientWidth -
                (getPixelPropertyValue(this.drawingToolContainer, 'padding-left') +
                    getPixelPropertyValue(this.drawingToolContainer, 'padding-right'))
        );
    }

    getAvailableHeight() {
        return (
            this.element &&
            this.element.clientHeight -
                (getPixelPropertyValue(this.element, 'padding-bottom') +
                    getPixelPropertyValue(this.element, 'padding-top'))
        );
    }

    setCanvasSize() {
        const {
            aspectRatio: { height: propsHeight, width: propsWidth },
        } = this.props;
        const aspectRatio = propsHeight / propsWidth;

        // sets a default availableWidth of baseCanvasSize in case for some reason
        // the ref for this.drawingToolContainer is not available.
        const availableWidth = this.getAvailableWidth() || baseCanvasSize;
        // sets a default availableHeight of baseCanvasSize * aspectRatio in case for some reason
        // the ref for this.element is not available.
        const availableHeight = this.getAvailableHeight() || baseCanvasSize * aspectRatio;

        let width;
        let height;

        if (availableWidth * aspectRatio <= availableHeight) {
            width = availableWidth;
            height = availableWidth * aspectRatio;
        } else {
            width = availableHeight / aspectRatio;
            height = availableHeight;
        }

        this.canvas.setWidth(width);
        this.canvas.setHeight(height);

        // We use baseCanvasSize as 100% scale and scale everything relative to that based on the available size
        const zoom = width / baseCanvasSize;
        this.canvas.setZoom(zoom);
    }

    getClassName(tool) {
        const { activeTool } = this.state;
        const className = `drawing-tool__button drawing-tool__button-${tool}`;
        const activeClassName = 'drawing-tool__button--active';
        return activeTool === tool ? `${className} ${activeClassName}` : className;
    }

    async setInitialState(props) {
        await new Promise(resolve => {
            if (props.initialState) {
                this.canvas.loadFromJSON(props.initialState, () => {
                    this.saveCanvasState('initialLoad');
                    this.setState({ loading: false }, resolve);
                });
            } else {
                this.saveCanvasState('initialLoad');
                this.setState({ loading: false }, resolve);
            }
        });
        await this.addBackgroundImage();
    }

    async initialiseCanvas() {
        const { fabric } = await import('fabric');
        const { isStatic } = this.props;

        const tools = await import('./tools');
        const Canvas = isStatic ? fabric.StaticCanvas : fabric.Canvas;

        this.canvas = new Canvas(this.id, {
            containerClass: 'drawing-tool__canvas',
            isDrawingMode: false,
            freeDrawingLineWidth: 5,
            preserveObjectStacking: true,
        });

        this.setState({
            fabric,
            tools: tools.default,
        });
    }

    initialiseCanvasEvents() {
        this.canvas.on('mouse:down', options => {
            if (options.target) {
                const {
                    fillColor: stateFillColor,
                    strokeColor: stateStrokeColor,
                    strokeWidth: stateStrokeWidth,
                } = this.state;

                const fillColor = options.target.fill
                    ? fillOptions.find(option => formatColor(options.target.fill) === formatColor(option.value))
                    : stateFillColor;

                const strokeColor = options.target.stroke
                    ? fillOptions.find(option => formatColor(options.target.stroke) === formatColor(option.value))
                    : stateStrokeColor;

                const strokeWidth = options.target.strokeWidth
                    ? strokeWidthOptions.find(option => options.target.strokeWidth === option.value)
                    : stateStrokeWidth;

                this.setState({ fillColor, strokeColor, strokeWidth });
            }
        });

        this.canvas.on('mouse:down', this.onCanvasClick);
        this.isMouseDown = false;
        this.canvas.on('mouse:up', this.onMouseUp);
        this.canvas.on('mouse:move', this.onMouseMove);
        this.canvas.on('text:changed', () => this.saveCanvasState());
        this.canvas.on('path:created', () => this.saveCanvasState());
    }

    async addBackgroundImage() {
        const { backgroundImageUrl } = this.props;
        const url = backgroundImageUrl;
        const { fabric } = this.state;

        // eslint-disable-next-line consistent-return
        return new Promise(resolve => {
            if (!url) return resolve();

            fabric.Image.fromURL(url, img => {
                img.scaleToWidth(baseCanvasSize);
                this.canvas.setBackgroundImage(img, () => {
                    this.canvas.renderAll();
                    resolve();
                });
            });
        });
    }

    bringToFront() {
        this.canvas.bringToFront(this.canvas.getActiveObject());
        this.saveCanvasState();
    }

    changeObjectOptionsRecursively(activeObject, option) {
        return activeObject.type !== 'group'
            ? activeObject.setOptions(option)
            : activeObject.forEachObject(o => this.changeObjectOptionsRecursively(o, option));
    }

    changeFillColor(option) {
        this.setState({
            fillColor: option,
        });

        const activeObjects = this.canvas.getActiveObjects();

        if (activeObjects.length > 0) {
            activeObjects.forEach(activeObject => {
                this.changeObjectOptionsRecursively(activeObject, { fill: option.value });
            });
            this.canvas.discardActiveObject().renderAll();
            this.saveCanvasState();
        }
    }

    changeStrokeColor(option) {
        this.setState({
            strokeColor: option,
        });
        const activeObjects = this.canvas.getActiveObjects();

        if (activeObjects.length > 0) {
            activeObjects.forEach(activeObject => {
                this.changeObjectOptionsRecursively(activeObject, { stroke: option.value });
            });
            this.canvas.discardActiveObject().renderAll();
            this.saveCanvasState();
        }
    }

    changeStrokeWidth(option) {
        this.setState({
            strokeWidth: option,
        });
        const activeObjects = this.canvas.getActiveObjects();

        if (activeObjects.length > 0) {
            activeObjects.forEach(activeObject => {
                this.changeObjectOptionsRecursively(activeObject, { strokeWidth: Number(option.value) });
            });
            this.canvas.discardActiveObject().renderAll();
            this.saveCanvasState();
        }
    }

    changeTextSize(option) {
        const activeObjects = this.canvas.getActiveObjects();

        this.setState({
            textSize: option,
        });

        if (activeObjects.length > 0) {
            activeObjects.forEach(activeObject => {
                activeObject.setOptions({ fontSize: Number(option.value) });
            });
            this.canvas.renderAll();
            this.saveCanvasState();
        } else {
            const blackFill = fillOptions.find(color => color.value === 'rgb(0, 0, 0)');
            const transparentStroke = strokeOptions.find(color => color.value === 'rgba(0, 0, 0, 0)');
            this.setState(
                {
                    fillColor: blackFill,
                    strokeColor: transparentStroke,
                },
                () => {
                    this.setActiveTool('text');
                },
            );
        }
    }

    changeStamp(option) {
        this.setState({
            selectedStamp: option,
        });
        this.setActiveTool('stamp');
    }

    async clear() {
        const { activeTool } = this.state;
        // this disables and enables current tool calling deactivate and activate
        this.setActiveTool(activeTool);
        this.canvas.clear();
        await this.addBackgroundImage();
        this.saveCanvasState();
    }

    delete() {
        if (!(this.canvas.getActiveObjects().length === 1 && this.canvas.defaultCursor === 'text')) {
            this.canvas.getActiveObjects().forEach(activeObject => {
                this.canvas.remove(activeObject);
            });
            this.canvas.discardActiveObject();
        }
        this.canvas.renderAll();
        this.saveCanvasState();
    }

    exitFullscreen() {
        this.setState(
            {
                fauxFullscreenActive: false,
            },
            () => {
                this.setCanvasSize();
            },
        );
    }

    goFullscreen() {
        this.setState(
            {
                fauxFullscreenActive: true,
            },
            () => {
                this.setCanvasSize();
            },
        );
    }

    replay(playStack, saveStack) {
        if (playStack.length > 0) {
            saveStack.push(this.canvasState);
            this.canvasState = playStack.pop();

            this.canvas.clear();
            this.canvas.loadFromJSON(this.canvasState, () => {
                this.canvas.renderAll();
            });
        }
    }

    saveCanvasState(event = 'canvasChanged') {
        this.redoStack = [];

        if (this.canvasState) {
            this.undoStack.push(this.canvasState);
        }
        const state = JSON.stringify(this.canvas.toJSON());
        this.canvasState = state;

        if (event !== 'initialLoad') {
            this.saveToStore(state);
        }
    }

    sendToBack() {
        this.canvas.sendToBack(this.canvas.getActiveObject());
        this.saveCanvasState();
    }

    undo() {
        this.replay(this.undoStack, this.redoStack);
    }

    redo() {
        this.replay(this.redoStack, this.undoStack);
    }

    deactivateCameraModal() {
        this.setState({ activeTool: null });
    }

    deactivateImageUploadModal() {
        this.setState({ activeTool: null });
    }

    isFullscreenSupported() {
        return (
            this.element.requestFullscreen ||
            this.element.mozRequestFullScreen ||
            this.element.webkitRequestFullscreen ||
            this.element.msRequestFullscreen
        );
    }

    render() {
        const { hideControls, isStatic, t, predeterminedImageUrls, frontEndComponents, aspectRatio } = this.props;
        const { Button, LoadingPlaceholder } = frontEndComponents;
        const {
            activeTool,
            error,
            fauxFullscreenActive,
            fillColor,
            isFullscreen,
            loading,
            selectedStamp,
            strokeColor,
            strokeWidth,
            textSize,
        } = this.state;

        if (error) {
            return (
                <p className="drawing-tool__error">
                    <Translation translationKey="general.generic_error" defaultValue="Sorry, an error occurred." />
                </p>
            );
        }

        const fullscreenControls =
            isFullscreen || fauxFullscreenActive ? (
                <DrawingToolButton
                    className="drawing-tool__button drawing-tool__button-exit-fullscreen"
                    onClick={this.exitFullscreen}
                    tooltipTitle={t('general.exit_full_screen.tooltipTitle', 'Full screen')}
                    tooltipText={t('general.exit_full_screen.tooltipText', 'Exit full screen view')}
                >
                    <SvgExitFullScreen />
                </DrawingToolButton>
            ) : (
                <DrawingToolButton
                    className="drawing-tool__button drawing-tool__button-fullscreen"
                    ariaLabel={t('general.enter_full_screen.altText', 'fullscreen')}
                    onClick={this.goFullscreen}
                    tooltipTitle={t('general.enter_full_screen.tooltipTitle', 'Full Screen')}
                    tooltipText={t('general.enter_full_screen.tooltipText', 'View the canvas using your whole screen')}
                >
                    <SvgFullScreen />
                </DrawingToolButton>
            );

        const drawingToolClassName = cx({
            'drawing-tool': true,
            'drawing-tool--no-controls': hideControls,
            'drawing-tool--fullscreen': fauxFullscreenActive,
        });

        const drawingToolFullscreenBackgroundClassName = cx({
            'drawing-tool__fullscreen-background': true,
            'drawing-tool__fullscreen-background--active': fauxFullscreenActive,
        });

        return (
            <ThemeProvider theme={this.app === 'middleSchool' ? middleSchoolTheme : theme}>
                <div>
                    {loading && (
                        <div className="drawing-tool__loading-container">
                            <p>
                                <Translation
                                    translationKey="general.drawing_loading"
                                    defaultValue="Loading your drawing. Please wait..."
                                />
                            </p>
                            <LoadingPlaceholder />
                        </div>
                    )}
                    <div className={loading ? 'drawing-tool__wrapper--hidden' : 'drawing-tool__wrapper'}>
                        <div className={drawingToolFullscreenBackgroundClassName} />
                        <div className={drawingToolClassName} ref={this.onRefChange}>
                            {activeTool === 'camera' && (
                                <DrawingToolModal title="Camera" deactivateModal={this.deactivateCameraModal}>
                                    <Camera
                                        canvas={this.canvas}
                                        onCapture={this.deactivateCameraModal}
                                        saveCanvasState={this.saveCanvasState}
                                        frontEndComponents={frontEndComponents}
                                    />
                                </DrawingToolModal>
                            )}
                            {activeTool === 'image-upload' && (
                                <DrawingToolModal
                                    title="Image upload"
                                    deactivateModal={this.deactivateImageUploadModal}
                                >
                                    <ImageUpload
                                        canvas={this.canvas}
                                        onUpload={this.deactivateImageUploadModal}
                                        saveCanvasState={this.saveCanvasState}
                                        frontEndComponents={frontEndComponents}
                                    />
                                </DrawingToolModal>
                            )}

                            {!hideControls && (
                                <div className="drawing-tool__toolbar">
                                    <div className="drawing-tool__toolbar-tools">
                                        {!isStatic && (
                                            <div>
                                                <div className="drawing-tool__button-group">
                                                    <DrawingToolButton
                                                        ariaLabel={t('general.tool_select.tooltipTitle', 'Select')}
                                                        ariaPressed={activeTool === 'select'}
                                                        className={this.getClassName('select')}
                                                        onClick={() => this.setActiveTool('select')}
                                                        tooltipTitle={t('general.tool_select.tooltipTitle', 'Select')}
                                                        tooltipText={t(
                                                            'general.tool_select.tooltipText',
                                                            'Select, move, rotate and re-size',
                                                        )}
                                                    >
                                                        <SvgSelect active={activeTool === 'select'} />
                                                    </DrawingToolButton>
                                                    <DrawingToolButton
                                                        ariaLabel={t(
                                                            'general.tool_duplicate.tooltipTitle',
                                                            'Duplicate',
                                                        )}
                                                        ariaPressed={activeTool === 'duplicate'}
                                                        className={this.getClassName('duplicate')}
                                                        onClick={() => {
                                                            this.setActiveTool('duplicate');
                                                        }}
                                                        tooltipTitle={t(
                                                            'general.tool_duplicate.tooltipTitle',
                                                            'Duplicate',
                                                        )}
                                                        tooltipText={t(
                                                            'general.tool_duplicate.tooltipText',
                                                            'Make a copy of the selection',
                                                        )}
                                                    >
                                                        <SvgDuplicate active={activeTool === 'duplicate'} />
                                                    </DrawingToolButton>
                                                </div>
                                                <div className="drawing-tool__button-group">
                                                    <DrawingToolButton
                                                        ariaLabel={t('general.tool_pencil.tooltipTitle', 'Pencil')}
                                                        ariaPressed={activeTool === 'pencil'}
                                                        className={this.getClassName('pencil')}
                                                        onClick={() => {
                                                            this.setActiveTool('pencil');
                                                        }}
                                                        tooltipTitle={t('general.tool_pencil.tooltipTitle', 'Pencil')}
                                                        tooltipText={t(
                                                            'general.tool_pencil.tooltipText',
                                                            'Draw freehand lines',
                                                        )}
                                                    >
                                                        <SvgPencil active={activeTool === 'pencil'} />
                                                    </DrawingToolButton>
                                                    <DrawingToolOptions
                                                        ariaLabel={[
                                                            t(
                                                                'general.tool_text.main_label',
                                                                'Text tool Add text and choose font size',
                                                            ),
                                                            t('general.tool_text.extra_small', 'Text size Extra small'),
                                                            t('general.tool_text.small', 'Text size Small'),
                                                            t('general.tool_text.medium', 'Text size Medium'),
                                                            t('general.tool_text.large', 'Text size Large'),
                                                            t('general.tool_text.extra_large', 'Text size Extra large'),
                                                        ]}
                                                        ariaPressed={activeTool === 'text'}
                                                        alt={t('general.tool_text', 'text')}
                                                        Icon={textIcon}
                                                        isActive={activeTool === 'text'}
                                                        options={textOptions}
                                                        update={this.changeTextSize}
                                                        value={textSize}
                                                        tooltipTitle={t('general.tool_text.tooltipTitle', 'Text')}
                                                        tooltipText={t('general.tool_text.tooltipText', 'Add text')}
                                                    />
                                                    <DrawingToolButton
                                                        ariaLabel={t('general.tool_line.tooltipTitle', 'Line')}
                                                        ariaPressed={activeTool === 'line'}
                                                        className={this.getClassName('line')}
                                                        onClick={() => {
                                                            this.setActiveTool('line');
                                                        }}
                                                        tooltipTitle={t('general.tool_line.tooltipTitle', 'Line')}
                                                        tooltipText={t(
                                                            'general.tool_line.tooltipText',
                                                            'Draw a straight line',
                                                        )}
                                                    >
                                                        <SvgLine active={activeTool === 'line'} />
                                                    </DrawingToolButton>
                                                    <DrawingToolButton
                                                        ariaLabel={t('general.tool_arrow.tooltipTitle', 'Arrow')}
                                                        ariaPressed={activeTool === 'arrow'}
                                                        className={this.getClassName('arrow')}
                                                        onClick={() => {
                                                            this.setActiveTool('arrow');
                                                        }}
                                                        tooltipTitle={t('general.tool_arrow.tooltipTitle', 'Arrow')}
                                                        tooltipText={t(
                                                            'general.tool_arrow.tooltipText',
                                                            'Draw an arrow',
                                                        )}
                                                    >
                                                        <SvgArrow active={activeTool === 'arrow'} />
                                                    </DrawingToolButton>
                                                    <DrawingToolButton
                                                        ariaLabel={t(
                                                            'general.tool_rectangle.tooltipTitle',
                                                            'Rectangle',
                                                        )}
                                                        ariaPressed={activeTool === 'rectangle'}
                                                        className={this.getClassName('rectangle')}
                                                        onClick={() => {
                                                            this.setActiveTool('rectangle');
                                                        }}
                                                        tooltipTitle={t(
                                                            'general.tool_rectangle.tooltipTitle',
                                                            'Rectangle',
                                                        )}
                                                        tooltipText={t(
                                                            'general.tool_rectangle.tooltipText',
                                                            'Draw a rectangle',
                                                        )}
                                                    >
                                                        <SvgRectangle active={activeTool === 'rectangle'} />
                                                    </DrawingToolButton>
                                                    <DrawingToolButton
                                                        ariaLabel={t('general.tool_ellipse.tooltipTitle', 'Ellipse')}
                                                        ariaPressed={activeTool === 'ellipse'}
                                                        className={this.getClassName('ellipse')}
                                                        onClick={() => {
                                                            this.setActiveTool('ellipse');
                                                        }}
                                                        tooltipTitle={t('general.tool_ellipse.tooltipTitle', 'Ellipse')}
                                                        tooltipText={t(
                                                            'general.tool_ellipse.tooltipText',
                                                            'Draw an ellipse',
                                                        )}
                                                    >
                                                        <SvgCircle active={activeTool === 'ellipse'} />
                                                    </DrawingToolButton>
                                                    <DrawingToolButton
                                                        ariaLabel={t(
                                                            'general.tool_curved_line.tooltipTitle',
                                                            'Curved Line',
                                                        )}
                                                        ariaPressed={activeTool === 'curvedLine'}
                                                        className={this.getClassName('curvedLine')}
                                                        onClick={() => {
                                                            this.setActiveTool('curvedLine');
                                                        }}
                                                        tooltipTitle={t(
                                                            'general.tool_curved_line.tooltipTitle',
                                                            'Curved Line',
                                                        )}
                                                        tooltipText={t(
                                                            'general.tool_curved_line.tooltipText',
                                                            'Draw a curved line',
                                                        )}
                                                    >
                                                        <SvgCurved active={activeTool === 'curvedLine'} />
                                                    </DrawingToolButton>
                                                    {!!predeterminedImageUrls.length && (
                                                        <FeatureFlag
                                                            flagKey="twig-drawing-tool-stamp-icon"
                                                            renderFeatureCallback={() => (
                                                                <DrawingToolOptions
                                                                    ariaLabel={t(
                                                                        'general.tool_stamp.tooltipTitle',
                                                                        'Stamp',
                                                                    )}
                                                                    ariaPressed={activeTool === 'stamp'}
                                                                    alt={t('general.tool_stamp', 'stamp')}
                                                                    Icon={stampIcon}
                                                                    isActive={activeTool === 'stamp'}
                                                                    options={stampOptions(predeterminedImageUrls)}
                                                                    shouldIconUpdate={false}
                                                                    update={this.changeStamp}
                                                                    value={selectedStamp}
                                                                    tooltipTitle={t(
                                                                        'general.tool_stamp.tooltipTitle',
                                                                        'Stamp',
                                                                    )}
                                                                    tooltipText={t(
                                                                        'general.tool_stamp.tooltipText',
                                                                        'Add stamps and symbols',
                                                                    )}
                                                                />
                                                            )}
                                                            renderDefaultCallback={() => null}
                                                        />
                                                    )}
                                                </div>
                                                <div className="drawing-tool__button-group">
                                                    <DrawingToolOptions
                                                        ariaLabel={[
                                                            t(
                                                                'general.tool_fill_color.main_label',
                                                                'Fill color tool Change color of selected area',
                                                            ),
                                                            t(
                                                                'general.tool_fill_color.no_fill',
                                                                'No fill Remove background color',
                                                            ),
                                                            t('general.tool_fill_color.white', 'Fill color White'),
                                                            t(
                                                                'general.tool_fill_color.light_gray',
                                                                'Fill color Light gray',
                                                            ),
                                                            t(
                                                                'general.tool_fill_color.mid_gray',
                                                                'Fill color Mid gray',
                                                            ),
                                                            t(
                                                                'general.tool_fill_color.dark_gray',
                                                                'Fill color Dark gray',
                                                            ),
                                                            t('general.tool_fill_color.black', 'Fill color Black'),
                                                            t('general.tool_fill_color.cream', 'Fill color Cream'),
                                                            t(
                                                                'general.tool_fill_color.light_brown',
                                                                'Fill color Light brown',
                                                            ),
                                                            t(
                                                                'general.tool_fill_color.mid_brown',
                                                                'Fill color Mid brown',
                                                            ),
                                                            t(
                                                                'general.tool_fill_color.warm_brown',
                                                                'Fill color Warm brown',
                                                            ),
                                                            t(
                                                                'general.tool_fill_color.dark_brown',
                                                                'Fill color Dark brown',
                                                            ),
                                                            t(
                                                                'general.tool_fill_color.very_dark_brown',
                                                                'Fill color Very dark brown',
                                                            ),
                                                            t('general.tool_fill_color.red', 'Fill color Red'),
                                                            t('general.tool_fill_color.orange', 'Fill color Orange'),
                                                            t(
                                                                'general.tool_fill_color.mid_yellow',
                                                                'Fill color Mid yellow',
                                                            ),
                                                            t(
                                                                'general.tool_fill_color.light_yellow',
                                                                'Fill color Light yellow',
                                                            ),
                                                            t(
                                                                'general.tool_fill_color.light_green',
                                                                'Fill color Light green',
                                                            ),
                                                            t(
                                                                'general.tool_fill_color.mid_green',
                                                                'Fill color Mid green',
                                                            ),
                                                            t(
                                                                'general.tool_fill_color.dark_green',
                                                                'Fill color Dark green',
                                                            ),
                                                            t(
                                                                'general.tool_fill_color.light_blue',
                                                                'Fill color Light blue',
                                                            ),
                                                            t(
                                                                'general.tool_fill_color.mid_blue',
                                                                'Fill color Mid blue',
                                                            ),
                                                            t(
                                                                'general.tool_fill_color.dark_blue',
                                                                'Fill color Dark blue',
                                                            ),
                                                            t(
                                                                'general.tool_fill_color.dark_purple',
                                                                'Fill color Dark purple',
                                                            ),
                                                            t(
                                                                'general.tool_fill_color.mid_purple',
                                                                'Fill color Mid purple',
                                                            ),
                                                            t('general.tool_fill_color.pink', 'Fill color Pink'),
                                                        ]}
                                                        alt={t('general.tool_fill_color', 'fill color')}
                                                        Icon={Fill}
                                                        options={fillOptions}
                                                        update={color => {
                                                            this.changeFillColor(color);
                                                            this.setActiveTool(activeTool);
                                                        }}
                                                        value={fillColor}
                                                        tooltipTitle={t(
                                                            'general.tool_fill_color.tooltipTitle',
                                                            'Fill Color',
                                                        )}
                                                        tooltipText={t(
                                                            'general.tool_fill_color.tooltipText',
                                                            'Change the fill color of the selection',
                                                        )}
                                                    />
                                                    <DrawingToolOptions
                                                        ariaLabel={[
                                                            t(
                                                                'general.tool_stroke_color.main_label',
                                                                'Line color Change line color of selected area',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_color.no_stroke',
                                                                'Line color Remove line color from selection',
                                                            ),
                                                            t('general.tool_stroke_color.white', 'Line color White'),
                                                            t(
                                                                'general.tool_stroke_color.light_gray',
                                                                'Line color Light gray',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_color.mid_gray',
                                                                'Line color Mid gray',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_color.dark_gray',
                                                                'Line color Dark gray',
                                                            ),
                                                            t('general.tool_stroke_color.black', 'Line color Black'),
                                                            t('general.tool_stroke_color.cream', 'Line color Cream'),
                                                            t(
                                                                'general.tool_stroke_color.light_brown',
                                                                'Line color Light brown',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_color.mid_brown',
                                                                'Line color Mid brown',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_color.warm_brown',
                                                                'Line color Warm brown',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_color.dark_brown',
                                                                'Line color Dark brown',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_color.very_dark_brown',
                                                                'Line color Very dark brown',
                                                            ),
                                                            t('general.tool_stroke_color.red', 'Line color Red'),
                                                            t('general.tool_stroke_color.orange', 'Line color Orange'),
                                                            t(
                                                                'general.tool_stroke_color.mid_yellow',
                                                                'Line color Mid yellow',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_color.light_yellow',
                                                                'Line color Light yellow',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_color.light_green',
                                                                'Line color Light green',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_color.mid_green',
                                                                'Line color Mid green',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_color.dark_green',
                                                                'Line color Dark green',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_color.light_blue',
                                                                'Line color Light blue',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_color.mid_blue',
                                                                'Line color Mid blue',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_color.dark_blue',
                                                                'Line color Dark blue',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_color.dark_purple',
                                                                'Line color Dark purple',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_color.mid_purple',
                                                                'Line color Mid purple',
                                                            ),
                                                            t('general.tool_stroke_color.pink', 'Line color Pink'),
                                                        ]}
                                                        alt={t('general.tool_stroke_color', 'stroke color')}
                                                        Icon={Stroke}
                                                        options={strokeOptions}
                                                        update={color => {
                                                            this.changeStrokeColor(color);
                                                            this.setActiveTool(activeTool);
                                                        }}
                                                        value={strokeColor}
                                                        tooltipTitle={t(
                                                            'general.tool_stroke_color.tooltipTitle',
                                                            'Line color',
                                                        )}
                                                        tooltipText={t(
                                                            'general.tool_stroke_color.tooltipText',
                                                            'Add text',
                                                        )}
                                                    />
                                                    <DrawingToolOptions
                                                        className="stroke-width"
                                                        ariaLabel={[
                                                            t(
                                                                'general.tool_stroke_width.tooltipTitle',
                                                                'Line thickness Change line width of selected area',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_width.extra_thin',
                                                                'Line thickness Extra thin',
                                                            ),
                                                            t('general.tool_stroke_width.thin', 'Line thickness Thin'),
                                                            t(
                                                                'general.tool_stroke_width.regular',
                                                                'Line thickness Regular',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_width.medium',
                                                                'Line thickness Medium',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_width.thick',
                                                                'Line thickness Thick',
                                                            ),
                                                            t(
                                                                'general.tool_stroke_width.extra_thick',
                                                                'Line thickness Extra thick',
                                                            ),
                                                        ]}
                                                        ariaPressed={activeTool === 'stroke-width'}
                                                        Icon={() => (
                                                            <SvgLineThickness active={activeTool === 'stroke-width'} />
                                                        )}
                                                        alt={t('general.tool_stroke_width', 'stroke width')}
                                                        shouldIconUpdate={false}
                                                        isActive={activeTool === 'stroke-width'}
                                                        update={width => {
                                                            this.setActiveTool('stroke-width');
                                                            this.changeStrokeWidth(width);
                                                        }}
                                                        value={strokeWidth}
                                                        options={strokeWidthOptions}
                                                        tooltipTitle={t(
                                                            'general.tool_stroke_width.tooltipTitle',
                                                            'Line thickness',
                                                        )}
                                                        tooltipText={t(
                                                            'general.tool_stroke_width.tooltipText',
                                                            'Change the line thickness of the selection',
                                                        )}
                                                    />
                                                </div>
                                                <div className="drawing-tool__button-group">
                                                    {'mediaDevices' in navigator && (
                                                        <DrawingToolButton
                                                            ariaLabel={t(
                                                                'general.tool_camera.tooltipTitle',
                                                                'Take a photo',
                                                            )}
                                                            ariaPressed={activeTool === 'camera'}
                                                            className={this.getClassName('camera')}
                                                            onClick={() => {
                                                                this.setActiveTool('camera');
                                                            }}
                                                            tooltipTitle={t(
                                                                'general.tool_camera.tooltipTitle',
                                                                'Take a photo',
                                                            )}
                                                            tooltipText={t(
                                                                'general.tool_camera.tooltipText',
                                                                // eslint-disable-next-line max-len
                                                                "Use your device's camera to take a photo and add it to the canvas",
                                                            )}
                                                        >
                                                            <SvgCamera active={activeTool === 'camera'} />
                                                        </DrawingToolButton>
                                                    )}
                                                    <DrawingToolButton
                                                        ariaLabel={t(
                                                            'general.tool_image_upload.tooltipTitle',
                                                            'Upload an image',
                                                        )}
                                                        ariaPressed={activeTool === 'image-upload'}
                                                        className={this.getClassName('image-upload')}
                                                        onClick={() => {
                                                            this.setActiveTool('image-upload');
                                                        }}
                                                        tooltipTitle={t(
                                                            'general.tool_image_upload.tooltipTitle',
                                                            'Upload an image',
                                                        )}
                                                        tooltipText={t(
                                                            'general.tool_image_upload.tooltipText',
                                                            'Upload an image from your device',
                                                        )}
                                                    >
                                                        <SvgImage active={activeTool === 'image-upload'} />
                                                    </DrawingToolButton>
                                                </div>
                                                <div className="drawing-tool__button-group">
                                                    <DrawingToolButton
                                                        ariaLabel={t('general.tool_undo.tooltipTitle', 'Undo')}
                                                        ariaPressed={activeTool === 'undo'}
                                                        className={this.getClassName('undo')}
                                                        onClick={() => {
                                                            this.setActiveTool('undo');
                                                            this.undo();
                                                        }}
                                                        tooltipTitle={t('general.tool_undo.tooltipTitle', 'Undo')}
                                                        tooltipText={t(
                                                            'general.tool_undo.tooltipText',
                                                            'Undo last action',
                                                        )}
                                                    >
                                                        <SvgUndo active={activeTool === 'undo'} />
                                                    </DrawingToolButton>
                                                    <DrawingToolButton
                                                        ariaLabel={t('general.tool_redo.tooltipTitle', 'Redo')}
                                                        ariaPressed={activeTool === 'redo'}
                                                        className={this.getClassName('redo')}
                                                        onClick={() => {
                                                            this.setActiveTool('redo');
                                                            this.redo();
                                                        }}
                                                        tooltipTitle={t('general.tool_redo.tooltipTitle', 'Redo')}
                                                        tooltipText={t(
                                                            'general.tool_redo.tooltipText',
                                                            'Redo last action',
                                                        )}
                                                    >
                                                        <SvgRedo active={activeTool === 'redo'} />
                                                    </DrawingToolButton>
                                                    <DrawingToolButton
                                                        ariaLabel={t(
                                                            'general.tool_bring_to_front.tooltipTitle',
                                                            'Bring forward',
                                                        )}
                                                        ariaPressed={activeTool === 'bring-to-front'}
                                                        className={this.getClassName('bring-to-front')}
                                                        onClick={() => {
                                                            this.setActiveTool('bring-to-front');
                                                            this.bringToFront();
                                                        }}
                                                        tooltipTitle={t(
                                                            'general.tool_bring_to_front.tooltipTitle',
                                                            'Bring forward',
                                                        )}
                                                        tooltipText={t(
                                                            'general.tool_bring_to_front.tooltipText',
                                                            'Bring selected item forward a layer',
                                                        )}
                                                    >
                                                        <SvgBringFront active={activeTool === 'bring-to-front'} />
                                                    </DrawingToolButton>
                                                    <DrawingToolButton
                                                        ariaLabel={t(
                                                            'general.tool_send_to_back.tooltipTitle',
                                                            'Bring backward',
                                                        )}
                                                        ariaPressed={activeTool === 'send-to-back'}
                                                        className={this.getClassName('send-to-back')}
                                                        onClick={() => {
                                                            this.setActiveTool('send-to-back');
                                                            this.sendToBack();
                                                        }}
                                                        tooltipTitle={t(
                                                            'general.tool_send_to_back.tooltipTitle',
                                                            'Bring backward',
                                                        )}
                                                        tooltipText={t(
                                                            'general.tool_send_to_back.tooltipText',
                                                            'Send selected item back a layer',
                                                        )}
                                                    >
                                                        <SvgSendBack active={activeTool === 'send-to-back'} />
                                                    </DrawingToolButton>
                                                </div>
                                                <div className="drawing-tool__button-group">
                                                    {fullscreenControls}
                                                    <DrawingToolButton
                                                        ariaLabel={t('general.tool_delete.tooltipTitle', 'Delete')}
                                                        ariaPressed={activeTool === 'delete'}
                                                        className={this.getClassName('delete')}
                                                        onClick={() => {
                                                            this.setActiveTool('delete');
                                                            this.delete();
                                                        }}
                                                        tooltipTitle={t('general.tool_delete.tooltipTitle', 'Delete')}
                                                        tooltipText={t(
                                                            'general.tool_delete.tooltipText',
                                                            'Delete the selection',
                                                        )}
                                                    >
                                                        <SvgDelete active={activeTool === 'delete'} />
                                                    </DrawingToolButton>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    {!isStatic && (
                                        <div>
                                            <Button
                                                // eslint-disable-next-line max-len
                                                className="drawing-tool__action-button drawing-tool__clear"
                                                onClick={this.clear}
                                                primary
                                                ariaLabel={t('general.clear_all', 'Clear All')}
                                                alt="Clear all button"
                                            >
                                                <Translation
                                                    translationKey="general.clear_all"
                                                    defaultValue="Clear All"
                                                />
                                            </Button>
                                        </div>
                                    )}
                                    {isStatic && fullscreenControls}
                                </div>
                            )}
                            {fauxFullscreenActive && (
                                <button
                                    className="drawing-tool__close-fullscreen"
                                    onClick={this.exitFullscreen}
                                    aria-label={t('general.aria_exit_full_screen', 'Close')}
                                    type="button"
                                >
                                    <img src={closeIcon} alt="close" />
                                </button>
                            )}

                            <div
                                className="drawing-tool__container"
                                ref={e => {
                                    this.drawingToolContainer = e;
                                }}
                            >
                                <canvas
                                    id={this.id}
                                    width={baseCanvasSize}
                                    height={baseCanvasSize * (aspectRatio.height / aspectRatio.width)}
                                />
                                <div className="drawing-tool-options__title">
                                    {!!predeterminedImageUrls.length && (
                                        <FeatureFlag
                                            flagKey="K6-drag-drop"
                                            renderFeatureCallback={() => (
                                                <DrawingToolDragDrop
                                                    ariaLabel={t('general.tool_stamp_drag.tooltipTitle', 'Image bank:')}
                                                    alt={t('general.tool_stamp_drag.altText', 'Image bank:')}
                                                    options={stampOptions(predeterminedImageUrls)}
                                                    shouldIconUpdate={false}
                                                    update={this.changeStamp}
                                                    value={selectedStamp}
                                                />
                                            )}
                                            renderDefaultCallback={() => null}
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </ThemeProvider>
        );
    }
}

DrawingTool.defaultProps = {
    aspectRatio: {
        height: 3,
        width: 4,
    },
    hideControls: false,
    isStatic: false,
    initialState: null,
    saveToStore: () => {},
    backgroundImageUrl: '',
    predeterminedImageUrls: [],
    onReady: null,
};

DrawingTool.propTypes = {
    aspectRatio: PropTypes.shape({
        height: PropTypes.number,
        width: PropTypes.number,
    }),
    hideControls: PropTypes.bool,
    isStatic: PropTypes.bool,
    saveToStore: PropTypes.func,
    initialState: PropTypes.shape({}),
    frontEndComponents: PropTypes.shape({
        Button: PropTypes.func.isRequired,
        LoadingPlaceholder: PropTypes.func.isRequired,
    }).isRequired,
    backgroundImageUrl: PropTypes.string,
    predeterminedImageUrls: PropTypes.arrayOf(PropTypes.string),
    t: PropTypes.func.isRequired,
    onReady: PropTypes.func,
};

export default DrawingTool;
